import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import { isTrustedPublicationFeatureEnabled } from "@/config/runtimeConfig";

const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const TARGET_ID_PATTERN = /^[A-Za-z0-9._:@-]{1,128}$/;
const CONTENT_KINDS = new Set(["socialPost", "mediaPost"]);
const MEDIA_KINDS = new Set([
  "image",
  "video",
  "audio",
  "embed",
  "poster",
  "tweet",
  "post",
  "documents",
]);
const SAFE_ERROR_MESSAGES = Object.freeze({
  "feature-disabled": "Trusted publication is not enabled.",
  "invalid-argument": "The publication request is invalid.",
  "invalid-response": "The publication service returned an invalid response.",
  unauthenticated: "Sign in before publishing.",
  "permission-denied": "This publication action is not available.",
  "failed-precondition": "The publication could not be finalized.",
  aborted: "The publication operation conflicted with another request.",
  unavailable: "The publication service is temporarily unavailable.",
  "deadline-exceeded": "The publication service did not respond in time.",
  unknown: "The publication operation could not be completed.",
});

export class TrustedPublicationServiceError extends Error {
  constructor(code = "unknown") {
    const safeCode = Object.prototype.hasOwnProperty.call(
      SAFE_ERROR_MESSAGES,
      code,
    )
      ? code
      : "unknown";
    super(SAFE_ERROR_MESSAGES[safeCode]);
    this.name = "TrustedPublicationServiceError";
    this.code = safeCode;
  }
}

function fail(code) {
  throw new TrustedPublicationServiceError(code);
}

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function assertExactKeys(value, expectedKeys) {
  if (
    !isRecord(value) ||
    Object.keys(value).sort().join(",") !== [...expectedKeys].sort().join(",")
  ) {
    fail("invalid-argument");
  }
}

function validateOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument");
  }
  return value;
}

function validateTargetId(value) {
  const targetId = typeof value === "string" ? value.trim() : "";
  const reservedFirestoreId =
    targetId === "." || targetId === ".." || /^__.*__$/.test(targetId);
  if (!TARGET_ID_PATTERN.test(targetId) || reservedFirestoreId) {
    fail("invalid-argument");
  }
  return targetId;
}

function validateCopy(value, maximumLength) {
  if (typeof value !== "string" || value.length > maximumLength) {
    fail("invalid-argument");
  }
  return value;
}

function validateFinalizeInput(value) {
  const fields = [
    "operationId",
    "postId",
    "organisationId",
    "contentKind",
    "mediaKind",
    "title",
    "text",
  ];
  assertExactKeys(value, fields);
  const organisationId =
    value.organisationId === null
      ? null
      : validateTargetId(value.organisationId);
  if (!CONTENT_KINDS.has(value.contentKind) || !MEDIA_KINDS.has(value.mediaKind)) {
    fail("invalid-argument");
  }
  const title = validateCopy(value.title, 200);
  const text = validateCopy(value.text, 2000);
  if (!title.trim() && !text.trim()) fail("invalid-argument");

  return Object.freeze({
    operationId: validateOperationId(value.operationId),
    postId: validateTargetId(value.postId),
    organisationId,
    contentKind: value.contentKind,
    mediaKind: value.mediaKind,
    title,
    text,
  });
}

function validateDeleteInput(value) {
  assertExactKeys(value, ["operationId", "postId"]);
  return Object.freeze({
    operationId: validateOperationId(value.operationId),
    postId: validateTargetId(value.postId),
  });
}

function validateTerminalResponse(value, result) {
  if (
    !isRecord(value) ||
    Object.keys(value).sort().join(",") !== "result,state" ||
    value.state !== "completed" ||
    value.result !== result
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    state: "completed",
    result,
  });
}

function normalizeRemoteError(error) {
  if (error instanceof TrustedPublicationServiceError) return error;
  const rawCode = typeof error?.code === "string" ? error.code : "";
  const code = rawCode.includes("/")
    ? rawCode.slice(rawCode.lastIndexOf("/") + 1)
    : rawCode;
  return new TrustedPublicationServiceError(code || "unknown");
}

function defaultCryptoProvider() {
  return typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
}

export function createPublicationOperationId(
  cryptoProvider = defaultCryptoProvider(),
) {
  let operationId = "";
  if (typeof cryptoProvider?.randomUUID === "function") {
    operationId = cryptoProvider.randomUUID();
  } else if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    operationId = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
  }
  return validateOperationId(operationId);
}

export function createTrustedPublicationService({
  functionsInstance = functions,
  callableFactory = httpsCallable,
  enabled = isTrustedPublicationFeatureEnabled,
} = {}) {
  const callables = new Map();
  const requireFeature = () => {
    if (enabled !== true) fail("feature-disabled");
  };
  const getCallable = (name) => {
    if (!callables.has(name)) {
      callables.set(name, callableFactory(functionsInstance, name));
    }
    return callables.get(name);
  };
  const invoke = async (name, payload, responseContext) => {
    try {
      const response = await getCallable(name)(payload);
      return validateTerminalResponse(response?.data, responseContext);
    } catch (error) {
      throw normalizeRemoteError(error);
    }
  };

  return Object.freeze({
    async finalizePublishedPost(input) {
      requireFeature();
      const payload = validateFinalizeInput(input);
      return invoke("finalizePublishedPost", payload, "finalized");
    },

    async deletePublishedPost(input) {
      requireFeature();
      const payload = validateDeleteInput(input);
      return invoke("deletePublishedPost", payload, "deleted");
    },
  });
}

export const trustedPublicationService = createTrustedPublicationService();
